import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  FaShoppingBag,
  FaCheck,
  FaPlay,
  FaCalendar,
  FaArrowLeft,
  FaCheckCircle,
  FaClock,
  FaFileAlt,
} from "react-icons/fa";

import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";
import LazyImage from "../../components/utils/LazyImage";
import {
  selectCartItems,
  selectCartLoading,
} from "../../store/slices/cart/slice";
import { addToCart } from "../../store/slices/cart/asyncThunks";
import { getCourseById } from "../../store/slices/courses/asyncThunks";
import {
  selectCourses,
  selectCoursesLoading,
  selectCoursesError,
} from "../../store/slices/courses/slice";
import { priceFormatter } from "../../utils/utils";
import {
  PageContainer,
  BackButton,
  CourseHeader,
  CourseImageContainer,
  CourseContent,
  CourseTitle,
  CourseDescription,
  PriceSection,
  Price,
  CTAButton,
  CourseDetails,
  DetailSection,
  SectionTitle,
  WhatYouLearn,
  LearningItem,
  CourseIncludes,
  IncludeItem,
  LoadingContainer,
  ErrorContainer,
  CourseSubtitle,
} from "./CourseDetailsPage.styles";
import { BASE_URL } from "../../api";

const CourseDetailsPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux selectors
  const courses = useSelector(selectCourses);
  const coursesLoading = useSelector(selectCoursesLoading);
  const coursesError = useSelector(selectCoursesError);
  const cartItems = useSelector(selectCartItems);
  const cartLoading = useSelector(selectCartLoading);

  // Local state
  const [course, setCourse] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);

  // Scroll to top when opening course details (or switching course)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [courseId]);

  // Find course from existing courses or fetch if not available
  // (URL parametar može biti slug ili UUID — backend podržava oba)
  useEffect(() => {
    const existingCourse = courses.find(
      (c) => c.slug === courseId || c.id === courseId,
    );
    if (existingCourse) {
      setCourse(existingCourse);
    } else {
      // Fetch course by slug/ID if not in store
      dispatch(getCourseById(courseId))
        .unwrap()
        .then((courseData) => {
          setCourse(courseData.course);
        })
        .catch((error) => {
          console.error("Error fetching course:", error);
        });
    }
  }, [courseId, courses, dispatch]);

  // Kanonski URL: ako je stranica otvorena preko starog UUID linka,
  // tiho zameni adresu slug verzijom (bez novog učitavanja)
  useEffect(() => {
    if (course?.slug && courseId !== course.slug) {
      navigate(`/kurs/${course.slug}`, { replace: true });
    }
  }, [course?.slug, courseId, navigate]);

  // Helper functions
  const isInCart = (courseId) => {
    return cartItems.some((item) => item.courseId === courseId);
  };

  // Handle add to cart
  const handleAddToCart = (courseId) => {
    dispatch(addToCart({ courseId }));
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  const materials = course?.materialsIncluded
    ? course?.materialsIncluded.split(",").map((item) => item.trim())
    : ["", "", "", ""];

  // Format course description to handle paragraphs and lists
  const formatDescription = (text) => {
    if (!text) return "";

    // Words/phrases to highlight
    const highlightWords = [
      "Lash",
      "Brow",
      "Lift",
      "A do Š",
      "početnicima",
      "profesionalcima",
      "teorijska videa",
      "pokazna videa",
      "kratkim i dugim trepavicama",
      "svetlim i tamnim obrvama",
      "farmanje",
      "detaljno",
      "Curl",
      "Hybrid",
      "Teardrop",
      "jastučića",
      "jastučići",
      "jastučićima",
      "veličina",
      "veličinu",
      "oblik",
      "Chart",
      "combo opcije",
      "higijene",
      "4 vrste",
      // Mini edukacija "Sve o jastučićima" (novi opis)
      "viši nivo",
      "tretman",
      "klijentu",
      "adekvatan",
      "Basic",
      "savršen",
      "game changer",
      "industriji",
      "usavrše",
      "detaljnog videa",
      "skripte",
      "Lifta",
      "trikove i savete",
    ];

    // Function to add highlights to text
    const addHighlights = (textContent) => {
      let highlightedText = textContent;

      highlightWords.forEach((word) => {
        const regex = new RegExp(`\\b${word}\\b`, "gi");
        highlightedText = highlightedText.replace(
          regex,
          `<span class="highlight">${word}</span>`,
        );
      });

      return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
    };

    // Split by "Sastoji se od:" to separate intro and list
    const parts = text.split("Sastoji se od:");

    if (parts.length === 2) {
      const intro = parts[0].trim();
      const listPart = parts[1].trim();

      // Split list items by '. ' (period followed by space)
      const listItems = listPart.split(". ").filter((item) => item.trim());

      return (
        <div>
          <p>{addHighlights(intro)}</p>
          <br />
          <p>
            <strong>Sastoji se od:</strong>
          </p>
          <ul>
            {listItems.map((item, index) => (
              <li key={index}>{addHighlights(item.trim())}</li>
            ))}
          </ul>
        </div>
      );
    }

    // If no "Sastoji se od:", just split by periods for paragraphs
    const sentences = text.split(". ").filter((sentence) => sentence.trim());
    return (
      <div>
        {sentences.map((sentence, index) => (
          <p key={index}>
            {addHighlights(sentence.trim())}
            {index < sentences.length - 1 ? "." : ""}
          </p>
        ))}
      </div>
    );
  };

  if (coursesLoading) {
    return (
      <MainContainer>
        <LoadingContainer>
          <div>Učitavanje kursa...</div>
        </LoadingContainer>
      </MainContainer>
    );
  }

  if (coursesError || !course) {
    return (
      <MainContainer>
        <ErrorContainer>
          <h2>Greška pri učitavanju kursa</h2>
          <p>Molimo pokušajte ponovo kasnije.</p>
          <CTAButton onClick={handleBack}>
            <FaArrowLeft />
            Nazad
          </CTAButton>
        </ErrorContainer>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <SEO
        title={course.title}
        description={
          course.shortDescription ||
          (course.description || "").slice(0, 160) ||
          `Online edukacija ${course.title} sa sertifikatom - MITOVKA BeautySpot.`
        }
        path={`/kurs/${course.slug || course.id}`}
        image={`${BASE_URL}/uploads/courses-thumbnails/${course.thumbnailImage}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: course.shortDescription || course.description,
          image: `${BASE_URL}/uploads/courses-thumbnails/${course.thumbnailImage}`,
          provider: {
            "@type": "Organization",
            name: "MITOVKA BeautySpot",
            url: "https://mitovka.com/",
          },
          offers: {
            "@type": "Offer",
            price: String(course.discountPrice || course.price),
            priceCurrency: course.currency || "RSD",
            availability: "https://schema.org/InStock",
            url: `https://mitovka.com/kurs/${course.slug || course.id}`,
            category: "Paid",
          },
          hasCourseInstance: {
            "@type": "CourseInstance",
            courseMode: "Online",
            ...(course.duration
              ? { courseWorkload: `PT${course.duration}M` }
              : {}),
          },
        }}
      />

      <PageContainer>
        <CourseHeader>
          <div style={{ flex: 1 }}>
            <BackButton onClick={handleBack}>
              <FaArrowLeft />
              Nazad
            </BackButton>

            <CourseImageContainer>
              <LazyImage
                src={`${BASE_URL}/uploads/courses-thumbnails/${course.thumbnailImage}`}
                alt={`${course.title} thumbnail`}
              />
            </CourseImageContainer>

            <CourseDetails>
              <DetailSection>
                <SectionTitle>Kurs uključuje</SectionTitle>
                <CourseIncludes>
                  {materials[0] && (
                    <IncludeItem>
                      <FaClock />
                      <span>{materials[0]}</span>
                    </IncludeItem>
                  )}

                  {materials[1] && (
                    <IncludeItem>
                      <FaPlay />
                      <span>{materials[1]}</span>
                    </IncludeItem>
                  )}

                  {materials[2] && (
                    <IncludeItem>
                      <FaFileAlt />
                      <span>{materials[2]}</span>
                    </IncludeItem>
                  )}

                  {materials[3] && (
                    <IncludeItem>
                      <FaCalendar />
                      <span>{materials[3]}</span>
                    </IncludeItem>
                  )}
                </CourseIncludes>
              </DetailSection>
              <DetailSection>
                <SectionTitle>Šta ćete naučiti</SectionTitle>
                <WhatYouLearn>
                  {course.learningOutcomes ? (
                    course.learningOutcomes
                      .split(". ")
                      .map((outcome, index) => (
                        <LearningItem key={index}>
                          <FaCheckCircle />
                          <span>{outcome.trim()}</span>
                        </LearningItem>
                      ))
                  ) : (
                    <>
                      <LearningItem>
                        <FaCheckCircle />
                        <span>
                          Savladaće osnovne tehnike makeup artistry-ja
                        </span>
                      </LearningItem>
                      <LearningItem>
                        <FaCheckCircle />
                        <span>Naučiće da koriste profesionalnu opremu</span>
                      </LearningItem>
                      <LearningItem>
                        <FaCheckCircle />
                        <span>Usvojiće veštine potrebne za samostalan rad</span>
                      </LearningItem>
                      <LearningItem>
                        <FaCheckCircle />
                        <span>Dobijati će praktične savete od stručnjaka</span>
                      </LearningItem>
                    </>
                  )}
                </WhatYouLearn>
              </DetailSection>
            </CourseDetails>
          </div>

          <CourseContent>
            <CourseTitle>{course.title}</CourseTitle>
            <CourseSubtitle>Online edukacija</CourseSubtitle>
            <CourseDescription>
              {formatDescription(course.description)}
            </CourseDescription>

            <PriceSection>
              <Price>
                {course.discountPrice && (
                  <span className="original-price">
                    {priceFormatter(course.price)} RSD
                  </span>
                )}
                <span className="current-price">
                  {priceFormatter(course.discountPrice || course.price)} RSD
                </span>
              </Price>

              <CTAButton
                onClick={() => handleAddToCart(course.id)}
                disabled={cartLoading}
                className={addedToCart || isInCart(course.id) ? "added" : ""}
              >
                {addedToCart ? (
                  <>
                    <FaCheck />
                    Dodato!
                  </>
                ) : isInCart(course.id) ? (
                  <>
                    <FaCheck />U Korpi
                  </>
                ) : (
                  <>
                    <FaShoppingBag />
                    Dodaj u Korpu
                  </>
                )}
              </CTAButton>
            </PriceSection>
          </CourseContent>
        </CourseHeader>
      </PageContainer>
    </MainContainer>
  );
};

export default CourseDetailsPage;
